import { useEffect, useState } from 'react'
import type { QcLot, QcResult } from '@alpha-edge/shared'
import { PageHeader, StatusBadge, colors, card, btnSecondary } from '../../components/ui'
import { api } from '../../services/api'

const CHART_WIDTH = 800
const CHART_HEIGHT = 300
const CHART_PADDING = 50

function LeveyJenningsChart({
  results,
  targetValue,
  targetSd,
}: {
  readonly results: readonly QcResult[]
  readonly targetValue: number
  readonly targetSd: number
}) {
  if (results.length === 0)
    return <p style={{ color: colors.textMuted, fontSize: '0.9375rem' }}>No QC results for this lot.</p>

  const sorted = [...results].sort((a, b) => new Date(a.performedAt).getTime() - new Date(b.performedAt).getTime())
  const maxPoints = Math.min(sorted.length, 40)
  const visible = sorted.slice(-maxPoints)

  const yMin = targetValue - 4 * targetSd
  const yMax = targetValue + 4 * targetSd
  const yRange = yMax - yMin

  const xStep = (CHART_WIDTH - 2 * CHART_PADDING) / Math.max(visible.length - 1, 1)
  const toY = (val: number) => CHART_PADDING + ((yMax - val) / yRange) * (CHART_HEIGHT - 2 * CHART_PADDING)
  const toX = (i: number) => CHART_PADDING + i * xStep

  const sdLines = [-3, -2, -1, 0, 1, 2, 3].map((n) => ({
    value: targetValue + n * targetSd,
    label: n === 0 ? 'Mean' : `${n > 0 ? '+' : ''}${n}SD`,
    color:
      Math.abs(n) === 3 ? colors.danger : Math.abs(n) === 2 ? colors.warning : n === 0 ? colors.primary : colors.border,
    dash: Math.abs(n) >= 2 ? '4,4' : '',
  }))

  return (
    <svg
      viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
      style={{
        width: '100%',
        maxWidth: CHART_WIDTH,
        border: `1px solid ${colors.border}`,
        borderRadius: '6px',
        background: '#fff',
      }}
    >
      {sdLines.map((line) => {
        const y = toY(line.value)
        return (
          <g key={line.label}>
            <line
              x1={CHART_PADDING}
              y1={y}
              x2={CHART_WIDTH - CHART_PADDING}
              y2={y}
              stroke={line.color}
              strokeWidth={1}
              strokeDasharray={line.dash}
            />
            <text x={CHART_PADDING - 6} y={y + 3} textAnchor="end" fontSize="10" fill={colors.textMuted}>
              {line.label}
            </text>
            <text
              x={CHART_WIDTH - CHART_PADDING + 6}
              y={y + 3}
              textAnchor="start"
              fontSize="10"
              fill={colors.textMuted}
            >
              {line.value.toFixed(1)}
            </text>
          </g>
        )
      })}

      {visible.length > 1 && (
        <polyline
          points={visible.map((r, i) => `${toX(i)},${toY(r.measuredValue)}`).join(' ')}
          fill="none"
          stroke={colors.primary}
          strokeWidth={1.5}
        />
      )}

      {visible.map((r, i) => {
        const cx = toX(i)
        const cy = toY(r.measuredValue)
        const fill = r.status === 'fail' ? colors.danger : r.status === 'warning' ? colors.warning : colors.success
        const hasViolation = r.westgardViolations && r.westgardViolations.length > 0
        return (
          <g key={r.id}>
            <circle cx={cx} cy={cy} r={hasViolation ? 6 : 4} fill={fill} stroke="#fff" strokeWidth={1.5} />
            {hasViolation && <circle cx={cx} cy={cy} r={9} fill="none" stroke={colors.danger} strokeWidth={1.5} />}
          </g>
        )
      })}

      {visible.map((r, i) => (
        <text
          key={`label-${r.id}`}
          x={toX(i)}
          y={CHART_HEIGHT - 8}
          textAnchor="middle"
          fontSize="8"
          fill={colors.textMuted}
          transform={`rotate(-45 ${toX(i)} ${CHART_HEIGHT - 8})`}
        >
          {new Date(r.performedAt).toLocaleDateString([], { month: 'short', day: 'numeric' })}
        </text>
      ))}
    </svg>
  )
}

export function QcLeveyJenningsPage() {
  const [lots, setLots] = useState<readonly QcLot[]>([])
  const [selectedLotId, setSelectedLotId] = useState<string | null>(null)
  const [results, setResults] = useState<readonly QcResult[]>([])
  const [loading, setLoading] = useState(true)
  const [resultsLoading, setResultsLoading] = useState(false)

  useEffect(() => {
    let cancelled = false
    api
      .get<readonly QcLot[]>('/qc/lots?limit=100')
      .then((res) => {
        if (cancelled) return
        const activeLots = (res.data ?? []).filter((l) => l.status === 'active')
        setLots(activeLots)
        if (activeLots.length > 0) setSelectedLotId(activeLots[0].id)
        setLoading(false)
      })
      .catch(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!selectedLotId) return
    let cancelled = false
    api
      .get<{ lot: QcLot; results: readonly QcResult[] }>(`/qc/lots/${selectedLotId}/results`)
      .then((res) => {
        if (cancelled) return
        setResults(res.data?.results ?? [])
        setResultsLoading(false)
      })
      .catch(() => {
        if (!cancelled) setResultsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [selectedLotId])

  const selectedLot = lots.find((l) => l.id === selectedLotId) ?? null

  return (
    <div>
      <PageHeader title="Levey-Jennings Charts" subtitle="QC trend analysis" />

      {loading ? (
        <div style={{ color: colors.textMuted }}>Loading lots...</div>
      ) : lots.length === 0 ? (
        <div style={{ ...card, textAlign: 'center', color: colors.textMuted }}>
          No active QC lots found. Create a lot first from QC &gt; Lots.
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
            {lots.map((lot) => (
              <button
                key={lot.id}
                type="button"
                onClick={() => setSelectedLotId(lot.id)}
                style={{
                  ...btnSecondary,
                  padding: '0.35rem 0.75rem',
                  fontSize: '0.8125rem',
                  background: lot.id === selectedLotId ? colors.primary : undefined,
                  color: lot.id === selectedLotId ? '#fff' : undefined,
                }}
              >
                {lot.lotNumber} - {lot.analyte}
              </button>
            ))}
          </div>

          {selectedLot && (
            <div style={{ ...card, marginBottom: '1rem' }}>
              <div style={{ display: 'flex', gap: '2rem', fontSize: '0.875rem', marginBottom: '1rem' }}>
                <span>
                  <strong>Lot:</strong> {selectedLot.lotNumber}
                </span>
                <span>
                  <strong>Analyte:</strong> {selectedLot.analyte}
                </span>
                <span>
                  <strong>Target:</strong> {selectedLot.targetValue} +/- {selectedLot.targetSd} {selectedLot.unit ?? ''}
                </span>
                <span>
                  <strong>Level:</strong> <StatusBadge status={selectedLot.level} />
                </span>
                <span>
                  <strong>Results:</strong> {results.length}
                </span>
              </div>

              {resultsLoading ? (
                <div style={{ color: colors.textMuted }}>Loading chart...</div>
              ) : (
                <LeveyJenningsChart
                  results={results}
                  targetValue={selectedLot.targetValue}
                  targetSd={selectedLot.targetSd}
                />
              )}

              {results.length > 0 && (
                <div style={{ marginTop: '0.75rem', fontSize: '0.8125rem', color: colors.textMuted }}>
                  Showing last {Math.min(results.length, 40)} data points. Red circles indicate Westgard violations.
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  )
}
